import { Injectable } from '@angular/core';
import {
  ListBasketInsightSort,
  ReportingApiService,
} from '@epicuro-next/data/reporting-api';
import { IUser, UsersApiService } from '@epicuro-next/data/users';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  AnalyticsSignalREvent,
  IListPlacedReportSignalrEvent,
} from '../../models/signalr-events';
import {
  loadListBasketInsight,
  loadListBasketInsightSuccess,
  reportDataReceived,
  upsertAnalyticsEntities,
} from '../analytics-entities.actions';
import { BASKET_INSIGHTS_PAGE_SIZE } from '../constants';
import {
  selectCanSeeAnalytics,
  selectCanSeeBasketInsights,
  selectListAnalyticsEntity,
} from '../selectors';

@Injectable()
export class AnalyticsBasketInsightsEffects {
  private canSeeBasketInsight$ = this.store.pipe(
    select(selectCanSeeBasketInsights),
    distinctUntilChanged(),
  );

  private canSeeAnalytics$ = this.store.pipe(
    select(selectCanSeeAnalytics),
    filter(isNonNullish),
    distinctUntilChanged(),
  );

  public loadListBasketInsights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadListBasketInsight),
      withLatestFrom(this.canSeeBasketInsight$, this.canSeeAnalytics$),
      filter(
        ([, canSeeBasketInsight, canSeeAnalytics]) =>
          canSeeAnalytics && canSeeBasketInsight,
      ),
      switchMap(([{ listId, sortBy }]) =>
        this.reportingService
          .getProductListBasketInsight({
            listId,
            topN: BASKET_INSIGHTS_PAGE_SIZE,
            sortBy,
          })
          .pipe(
            map((payload) => loadListBasketInsightSuccess({ payload })),
            catchError(() => [
              upsertAnalyticsEntities({
                entities: [
                  {
                    listId,
                    hasBasketInsightsError: true,
                  },
                ],
              }),
            ]),
          ),
      ),
    ),
  );

  public loadListBasketInsightsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadListBasketInsightSuccess),
      map(({ payload }) => payload),
      switchMap((payload) =>
        this.usersApiService
          .getUsersByExternalId(payload.baskets.map((basket) => basket.userId))
          .pipe(
            map((users) =>
              payload.baskets.map((basket) => ({
                ...basket,
                user: users.find(
                  (user) => user.externalId === basket.userId,
                ) as IUser,
              })),
            ),
            map((basketInsights) =>
              upsertAnalyticsEntities({
                entities: [
                  {
                    listId: payload.listId,
                    basketInsights,
                    numberOfBaskets:
                      payload.numberOfBaskets - BASKET_INSIGHTS_PAGE_SIZE,
                  },
                ],
              }),
            ),
            catchError(() => [
              upsertAnalyticsEntities({
                entities: [
                  {
                    listId: payload.listId,
                    hasBasketInsightsError: true,
                  },
                ],
              }),
            ]),
          ),
      ),
    ),
  );

  public reloadBasketInsights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reportDataReceived),
      concatMap(({ event }) =>
        of(event).pipe(
          filter(
            ({ eventName }) =>
              isNonNullish(eventName) &&
              eventName === AnalyticsSignalREvent.ListPlacedReportEvent,
          ),
          concatMap(({ payload }: IListPlacedReportSignalrEvent) =>
            of(payload).pipe(
              withLatestFrom(
                this.store
                  .pipe(
                    select(selectListAnalyticsEntity, payload.productListId),
                  )
                  .pipe(filter(isNonNullish)),
              ),
              map(([, entity]) =>
                loadListBasketInsight({
                  listId: entity.listId,
                  sortBy:
                    entity.basketInsightsSort ||
                    ListBasketInsightSort.MostRevenue,
                }),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private reportingService: ReportingApiService,
    private store: Store,
    private usersApiService: UsersApiService,
  ) {}
}
